import React from 'react';

import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import { AICompetitiveIntelligenceType } from 'types/recommendations';

import { AICI_REGENERATION_FEATURE_OPTIONS, OVERALL_AI_CI_LABEL } from './constants';
import { formatLastGenerated } from './helpers';

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: minmax(0, 1fr);
  gap: 40px;
  height: 100%;
  box-sizing: border-box;
  padding: 20px 0 25px;
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  font-size: 16px;
  line-height: 1.8;
`;

const Note = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;

  > * {
    flex-shrink: 0;
  }
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px 10px;
  font-size: 14px;
  color: var(--text-secondary);
`;

const SectionHeader = styled.div`
  padding: 6px 15px;
  background-color: var(--light-gray);
  font-size: 12px;

  &.spaced {
    margin-top: 14px;
  }
`;

const SectionNote = styled.div`
  padding: 10px 15px 4px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--text-secondary);
`;

const Item = styled(Checkbox)`
  &&& {
    align-items: flex-start;
    padding: 8px 15px 0;
    margin: 0;

    &.overall {
      margin-top: 8px;
      border-top: 1px solid var(--line-gray);

      .ant-checkbox-checked .ant-checkbox-inner {
        border-color: var(--gray-dark);
        background-color: var(--gray-dark) !important;
      }
    }

    .ant-checkbox {
      align-self: flex-start;
      margin-top: 4px;
    }
  }
`;

const SelectAllCheckbox = styled(Checkbox)`
  font-size: 16px;
`;

const ItemLabel = styled.div`
  font-size: 15px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  line-height: 1.2;

  &.disabled {
    color: var(--text-secondary);
  }
`;

const ItemDate = styled.div`
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.3;
  color: var(--text-secondary);
`;

type Props = {
  data?: AICompetitiveIntelligenceType;
  categoryConfigIds: number[];
  features: string[];
  onCategoryToggle: (configId: number, checked: boolean) => void;
  onFeatureToggle: (feature: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
};

const AICIRegenerateSelection = ({
  data,
  categoryConfigIds,
  features,
  onCategoryToggle,
  onFeatureToggle,
  onToggleAll,
}: Props) => {
  const relatedData = data?.related_data ?? [];
  const totalCount = relatedData.length + AICI_REGENERATION_FEATURE_OPTIONS.length;
  const selectedCount = categoryConfigIds.length + features.length;

  return (
    <Body>
      <Description>
        <div>
          Choose what to regenerate. We regenerate the AI CI analysis for each selected category, then automatically
          regenerate Overall AI CI so it reflects the latest category results. Action Plans and Sales Talk Tracks are
          built from these results, so they’re selected automatically and regenerate last. You can untick them.
        </div>
        <Note>Current results stay visible until the new ones are ready.</Note>
      </Description>
      <List data-test-id="aici-regenerate-config-list">
        <SelectAllRow>
          <SelectAllCheckbox
            className="green"
            checked={selectedCount === totalCount}
            indeterminate={Boolean(selectedCount) && selectedCount < totalCount}
            onChange={(event: any) => onToggleAll(event.target.checked)}
          >
            Select all
          </SelectAllCheckbox>
          <span>
            {selectedCount} of {totalCount} selected
          </span>
        </SelectAllRow>
        <SectionHeader>AI CI categories</SectionHeader>
        {relatedData.map((item) => (
          <Item
            key={item.config_id}
            className="green"
            checked={categoryConfigIds.includes(item.config_id)}
            onChange={(event: any) => onCategoryToggle(item.config_id, event.target.checked)}
          >
            <ItemLabel>{item.display_label}</ItemLabel>
            <ItemDate>{formatLastGenerated(item.last_generated_at)}</ItemDate>
          </Item>
        ))}
        <Item className="green overall" checked={Boolean(categoryConfigIds.length)} disabled>
          <ItemLabel className="disabled">{OVERALL_AI_CI_LABEL}</ItemLabel>
          <ItemDate>
            {formatLastGenerated(data?.last_generated_at)} · Regenerated automatically when any category is selected
          </ItemDate>
        </Item>
        <SectionHeader className="spaced">AI features using AI CI</SectionHeader>
        <SectionNote>
          Selected automatically when you choose a category, so they use the new results. Untick any you don’t want to
          regenerate.
        </SectionNote>
        {AICI_REGENERATION_FEATURE_OPTIONS.map((option) => (
          <Item
            key={option.value}
            className="green"
            checked={features.includes(option.value)}
            onChange={(event: any) => onFeatureToggle(option.value, event.target.checked)}
          >
            <ItemLabel>{option.label}</ItemLabel>
            <ItemDate>{formatLastGenerated(data?.[option.dateKey])}</ItemDate>
          </Item>
        ))}
      </List>
    </Body>
  );
};

export default AICIRegenerateSelection;
